import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductImageGallery from '../../components/ProductImageGallery';
import { useCart } from '../../context/CartContext';

const KaraokeSpeakerAndMic = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImage, setCurrentImage] = useState('');
  const [selectedColor, setSelectedColor] = useState('PINK');
  const [quantity, setQuantity] = useState(1);
  
  // 获取卡拉OK音响产品数据
  const product = {
    id: 25,
    name: "Karaoke Speaker with Dual Wireless Microphones & RGB Surround Lighting",
    category: "audio",
    price: "S$22.90",
    originalPrice: "S$39.99",
    image: "/images/shop-products/karaoke-speaker-and-mic/1.jpg",
    description: "Portable karaoke system with dual wireless microphones and powerful speakers for ultimate party experience",
    specs: {
      "Power": "100W",
      "Battery": "5000mAh",
      "Connectivity": "Bluetooth 5.0 + AUX + USB",
      "Microphones": "Dual Wireless",
      "Range": "10m",
      "Lighting": "RGB Surround",
      "Charging": "USB-C",
      "Playback Time": "8-12 hours",
      "Weight": "2.5kg",
      "Dimensions": "30 x 20 x 15 cm"
    },
    features: [
      "Dual Wireless Microphones",
      "100W Powerful Sound", 
      "RGB Surround Lighting",
      "Bluetooth 5.0",
      "Long Battery Life",
      "Portable Design"
    ],
    inStock: true,
    rating: 4.7,
    reviews: 234
  };
  
  // 颜色图片映射
  const colorImages = useMemo(() => ({
    'BEIGE': '/images/products/karaoke-speaker/beige .jpg',
    'PINK': '/images/products/karaoke-speaker/pink.jpg'
  }), []);
  
  // 更多图片 - 按顺序排列
  const additionalImages = [
    '/images/shop-products/karaoke-speaker-and-mic/1.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/2.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/3.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/4.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/5.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/6.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/7.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/8.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/9.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/10.jpg',
    '/images/shop-products/karaoke-speaker-and-mic/11.jpg'
  ];

  // 初始化当前图片
  useEffect(() => {
    setCurrentImage('/images/shop-products/karaoke-speaker-and-mic/1.jpg');
  }, []);

  // 当颜色改变时更新图片
  useEffect(() => {
    if (selectedColor && colorImages[selectedColor]) {
      setCurrentImage(colorImages[selectedColor]);
    } else {
      // 如果没有选择颜色，显示主图
      setCurrentImage('/images/shop-products/karaoke-speaker-and-mic/1.jpg');
    }
  }, [selectedColor, colorImages]);

  // 相关产品
  const relatedProducts = [
    {
      id: 1,
      name: "Wireless Gaming Headset",
      price: "S$89.99",
      image: "/images/products/placeholder.jpg"
    },
    {
      id: 2,
      name: "RGB Gaming Mouse Pad",
      price: "S$29.99",
      image: "/images/products/placeholder.jpg"
    },
    {
      id: 3,
      name: "Mechanical Gaming Keyboard",
      price: "S$129.99",
      image: "/images/products/placeholder.jpg"
    },
    {
      id: 4,
      name: "Gaming Controller",
      price: "S$59.99",
      image: "/images/products/placeholder.jpg"
    }
  ];

  // 添加到购物车函数
  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      color: selectedColor,
      connectivity: 'Wireless (with 2 mic)',
      image: currentImage
    };
    
    console.log('Adding to cart:', cartItem);
    addToCart(cartItem);
  };

  // 立即购买函数
  const handleBuyNow = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      color: selectedColor,
      connectivity: 'Wireless (with 2 mic)',
      image: currentImage
    };
    
    addToCart(cartItem);
    navigate('/cart');
  };

  return (
    <div className="container mx-auto px-4 py-12 text-white">
      {/* 面包屑导航 */}
      <div className="text-sm text-gray-400 mb-6">
        <Link to="/" className="hover:text-white">Home</Link> / 
        <Link to="/shop" className="hover:text-white mx-2">Shop</Link> / 
        <Link to="/shop/audio" className="hover:text-white mr-2">Audio</Link> / 
        <span className="text-white">Karaoke Speaker</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* 左侧产品图片 */}
        <div>
          <ProductImageGallery
            images={additionalImages}
            currentImage={currentImage}
            onImageChange={setCurrentImage}
          />
        </div>
        
        {/* 右侧产品信息 */}
        <div>
          <h1 className="text-3xl font-bold mb-2">Karaoke Speaker with Dual Wireless Microphones & RGB Surround Lighting</h1>
          
          {/* 评分 */}
          <div className="flex items-center mb-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.floor(product.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
              ))}
            </div>
            <span className="ml-2 text-gray-400">({product.reviews} reviews)</span>
          </div>
          
          {/* 价格 */}
          <div className="mb-6">
            <span className="text-3xl font-bold">S$22.90</span>
            <span className="ml-3 text-gray-400 line-through">S$39.99</span>
            <span className="ml-3 bg-red-600 text-white px-2 py-1 rounded-md text-sm">
              Save S$17.09
            </span>
          </div>
          
          {/* 库存状态 */}
          <div className="mb-6">
            {product.inStock ? (
              <span className="text-green-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                In Stock
              </span>
            ) : (
              <span className="text-red-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Out of Stock
              </span>
            )}
          </div>
          
          {/* 简短描述 */}
          <p className="text-gray-300 mb-6">{product.description}</p>
          
          {/* 选择颜色 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Choose Colour</h3>
            <div className="flex gap-3">
              {[
                { name: 'BEIGE', image: '/images/products/karaoke-speaker/beige .jpg' },
                { name: 'PINK', image: '/images/products/karaoke-speaker/pink.jpg' }
              ].map((color) => (
                <div key={color.name} className="flex flex-col">
                  <button 
                    className={`w-[80px] h-[80px] border ${color.name === selectedColor ? 'border-2 border-green-500' : 'border border-gray-600'} 
                      rounded-md bg-gray-800 flex items-center justify-center overflow-hidden relative`}
                    onClick={() => {
                      setSelectedColor(color.name);
                      setCurrentImage(color.image);
                    }}
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <img 
                        src={color.image}
                        alt={color.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {color.name === selectedColor && (
                      <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-r-[20px] border-t-transparent border-r-green-500"></div>
                    )}
                  </button>
                  <div className="mt-2 text-center">
                    <div className="text-sm text-white font-medium">{color.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* 选择连接方式 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Connectivity</h3>
            <div className="flex">
              <button 
                className="border-2 border-green-500 rounded-md py-3 px-4 text-center bg-green-500/10 text-green-500"
              >
                Wireless (with 2 mic)
              </button>
            </div>
          </div>
          
          {/* 数量 */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-3">Quantity</h3>
            <div className="flex items-center">
              <button 
                className="bg-gray-800 px-4 py-2 rounded-l-md"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                -
              </button>
              <input 
                type="text" 
                value={quantity} 
                readOnly
                className="bg-gray-800 text-center w-16 py-2 border-x border-gray-700"
              />
              <button 
                className="bg-gray-800 px-4 py-2 rounded-r-md"
                onClick={() => setQuantity(quantity + 1)}
              >
                +
              </button>
            </div>
          </div>
          
          {/* 购买按钮 */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button 
              className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-md font-medium cursor-pointer"
              onClick={handleAddToCart}
            >
              Add to Cart
            </button>
            <button 
              className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium border border-gray-600 cursor-pointer"
              onClick={handleBuyNow}
            >
              Buy Now
            </button>
          </div>
          
          {/* 配送信息 */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
              </svg>
              <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-300">Available Shipping</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 产品详情选项卡 */}
      <div className="mt-16">
        <div className="border-b border-gray-700 mb-8">
          <div className="flex space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-2 font-medium ${activeTab === 'overview' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specs')}
              className={`pb-4 px-2 font-medium ${activeTab === 'specs' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Specifications
            </button>
            <button 
              onClick={() => setActiveTab('reviews')}
              className={`pb-4 px-2 font-medium ${activeTab === 'reviews' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Reviews ({product.reviews})
            </button>
          </div>
        </div>
        
        {/* 选项卡内容 */}
        <div>
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Karaoke Speaker Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <p className="text-gray-300 mb-6">
                    The Karaoke Speaker with Dual Wireless Microphones & RGB Surround Lighting represents the ultimate party entertainment solution. 
                    Featuring dual wireless microphones and vibrant RGB lighting, this speaker delivers powerful audio performance 
                    while creating an immersive party atmosphere.
                  </p>
                  <p className="text-gray-300 mb-6">
                    With 100W power output and dual wireless microphones, you'll experience crystal-clear karaoke performance 
                    that brings your party to life. The RGB surround lighting automatically syncs with the music, 
                    creating a dynamic visual experience that enhances the entertainment.
                  </p>
                  <p className="text-gray-300">
                    The long-lasting battery ensures extended party sessions, while the wireless connectivity 
                    provides freedom of movement for singers. Perfect for home parties, outdoor events, and karaoke nights.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Key Features</h3>
                  <ul className="space-y-4">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium">{feature}</span>
                          <p className="text-gray-400 text-sm mt-1">
                            {feature === "Dual Wireless Microphones" && "Two wireless microphones for duet performances and group karaoke"}
                            {feature === "100W Powerful Sound" && "Powerful audio performance for large venues and outdoor events"}
                            {feature === "RGB Surround Lighting" && "Dynamic lighting that syncs with music for immersive party atmosphere"}
                            {feature === "Bluetooth 5.0" && "Latest Bluetooth technology for stable wireless connection"}
                            {feature === "Long Battery Life" && "Extended party sessions without worrying about charging"}
                            {feature === "Portable Design" && "Easy to carry and set up anywhere for instant entertainment"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'specs' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Karaoke Speaker Specifications</h2>
              <div className="bg-gray-800 rounded-lg p-6">
                <table className="w-full">
                  <tbody>
                    {Object.entries(product.specs).map(([key, value], index) => (
                      <tr key={index} className={index !== 0 ? "border-t border-gray-700" : ""}>
                        <td className="py-4 pr-4 font-medium w-1/3">{key}</td>
                        <td className="py-4 text-gray-300">{value}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Weight</td>
                      <td className="py-4 text-gray-300">320g</td>
                    </tr>
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Cable Length</td>
                      <td className="py-4 text-gray-300">1.5m (detachable)</td>
                    </tr>
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Warranty</td>
                      <td className="py-4 text-gray-300">2 Years Limited</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Karaoke Speaker Reviews</h2>
              <div className="flex items-center mb-8">
                <div className="mr-4">
                  <div className="text-5xl font-bold">{product.rating}</div>
                  <div className="flex text-yellow-400 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < Math.floor(product.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Based on {product.reviews} reviews</div>
                </div>
                <div className="flex-1 ml-6">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="flex items-center mb-2">
                      <div className="w-12 text-sm text-gray-400">{star} stars</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mx-2">
                        <div 
                          className="bg-yellow-400 h-2 rounded-full" 
                          style={{ 
                            width: `${star === 5 ? 65 : star === 4 ? 25 : star === 3 ? 8 : star === 2 ? 2 : 0}%` 
                          }}
                        ></div>
                      </div>
                      <div className="w-12 text-sm text-right text-gray-400">
                        {star === 5 ? '65%' : star === 4 ? '25%' : star === 3 ? '8%' : star === 4 ? '2%' : '0%'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 示例评论 */}
              <div className="space-y-6">
                {[
                  { name: "David M.", rating: 5, date: "1 week ago", comment: "This karaoke speaker is absolutely incredible! The dual wireless microphones work perfectly and the RGB lighting creates an amazing party atmosphere. Sound quality is crystal clear and the battery lasts for hours." },
                  { name: "Lisa T.", rating: 5, date: "2 weeks ago", comment: "I'm a party host and this speaker has transformed my events. The dual microphones are perfect for duets and group singing. The RGB lighting automatically syncs with music - it's like having a mini KTV at home!" },
                  { name: "Robert K.", rating: 4, date: "1 month ago", comment: "Great karaoke speaker overall. The audio quality is excellent and the wireless microphones have good range. The only minor issue is the RGB lighting could be more customizable, but the hardware is solid." }
                ].map((review, index) => (
                  <div key={index} className="border-b border-gray-700 pb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{review.name}</span>
                      <span className="text-gray-400 text-sm">{review.date}</span>
                    </div>
                    <div className="flex text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < review.rating ? "text-yellow-400" : "text-gray-600"}>★</span>
                      ))}
                    </div>
                    <p className="text-gray-300">{review.comment}</p>
                  </div>
                ))}
              </div>
              
              {/* 查看更多评论按钮 */}
              <div className="mt-8 text-center">
                <button className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium">
                  Load More Reviews
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 相关产品 */}
      <div className="mt-20">
        <h2 className="text-2xl font-bold mb-8">Related Audio & Party Products</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedProducts.map(product => (
            <div key={product.id} className="bg-gray-800 rounded-lg overflow-hidden">
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-medium mb-2">{product.name}</h3>
                <div className="flex justify-between items-center">
                  <span className="font-bold">{product.price}</span>
                  <button className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded-md text-sm">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KaraokeSpeakerAndMic;